/**
 * Traduction des échecs réseau en messages exploitables par un chauffeur.
 *
 * Les écrans mobiles avalaient les erreurs (`catch { /* offline gracieux *\/ }`) ou les
 * réduisaient à « Erreur. Réessayez. ». Le chauffeur ne pouvait pas distinguer un tunnel sans
 * réseau d'une session expirée ni d'une panne serveur, et ne savait donc pas s'il devait
 * réessayer, se reconnecter ou appeler le back-office.
 */

/**
 * Refus exprimé par le backend dans un corps de réponse 200 (`success: false`), et non par un
 * statut HTTP. Distinguer ce cas évite de confondre son message — déjà rédigé pour l'utilisateur —
 * avec celui d'une véritable erreur de transport.
 */
export class ErreurMetier extends Error {}

export type CategorieErreur =
    | 'HORS_RESEAU'
    | 'DELAI_DEPASSE'
    | 'NON_AUTHENTIFIE'
    | 'NON_AUTORISE'
    | 'SERVEUR'
    | 'REQUETE'
    | 'INCONNUE';

export interface ErreurReseau {
    categorie: CategorieErreur;
    /** Texte affiché, écrit pour être lu d'un coup d'œil au volant. */
    message: string;
    /** Un nouvel essai a une chance d'aboutir sans autre action de l'utilisateur. */
    reessayable: boolean;
}

/** Statut HTTP de la réponse, ou undefined si aucune réponse n'est parvenue. */
function statut(erreur: any): number | undefined {
    return erreur?.response?.status;
}

/**
 * Message renvoyé par le backend, s'il est exploitable. Les contrôleurs préfixent leurs erreurs
 * par « Erreur serveur: » suivi du message d'exception : illisible pour un chauffeur, on ne le
 * reprend donc pas tel quel.
 */
function messageServeur(erreur: any): string | undefined {
    const brut = erreur?.response?.data?.message;
    if (typeof brut !== 'string' || !brut.trim() || brut.startsWith('Erreur serveur:')) {
        return undefined;
    }
    return brut;
}

export function classerErreur(erreur: any): ErreurReseau {
    if (erreur instanceof ErreurMetier) {
        return { categorie: 'REQUETE', message: erreur.message, reessayable: false };
    }

    const code = statut(erreur);

    if (code === undefined) {
        // Aucune réponse : soit le délai a expiré, soit la requête n'est jamais partie.
        if (erreur?.code === 'ECONNABORTED' || erreur?.code === 'ETIMEDOUT') {
            return {
                categorie: 'DELAI_DEPASSE',
                message: 'Le serveur met trop de temps à répondre. Réseau faible ? Réessayez.',
                reessayable: true,
            };
        }
        return {
            categorie: 'HORS_RESEAU',
            message: navigator.onLine
                ? 'Serveur injoignable. Vérifiez votre connexion et réessayez.'
                : 'Pas de réseau. Reconnectez-vous à Internet puis réessayez.',
            reessayable: true,
        };
    }

    if (code === 401) {
        return {
            categorie: 'NON_AUTHENTIFIE',
            message: 'Session expirée. Reconnectez-vous pour continuer.',
            reessayable: false,
        };
    }

    if (code === 403) {
        return {
            categorie: 'NON_AUTORISE',
            message: "Accès refusé : cette mission ne vous est pas attribuée.",
            reessayable: false,
        };
    }

    if (code >= 500) {
        return {
            categorie: 'SERVEUR',
            message: 'Le serveur a rencontré une erreur. Prévenez le back-office si cela persiste.',
            reessayable: true,
        };
    }

    if (code >= 400) {
        return {
            categorie: 'REQUETE',
            message: messageServeur(erreur) || 'Action refusée par le serveur.',
            reessayable: false,
        };
    }

    return {
        categorie: 'INCONNUE',
        message: messageServeur(erreur) || 'Une erreur inattendue est survenue.',
        reessayable: true,
    };
}
